/**
 * Converts raw pose data to an LLM-friendly format for basketball coaching analysis.
 * Transforms MediaPipe BlazePose landmarks into human-readable joint angles and movements.
 * Ported from TypeScript to JavaScript
 */

/**
 * MediaPipe BlazePose landmark indices
 */
const LANDMARK_INDICES = {
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_PINKY: 17,
  RIGHT_PINKY: 18,
  LEFT_INDEX: 19,
  RIGHT_INDEX: 20,
  LEFT_THUMB: 21,
  RIGHT_THUMB: 22,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32,
};

/**
 * Human-readable joint angle names
 */
const JOINT_NAMES = {
  left_elbow: 'Left Elbow',
  right_elbow: 'Right Elbow',
  left_shoulder: 'Left Shoulder',
  right_shoulder: 'Right Shoulder',
  left_hip: 'Left Hip',
  right_hip: 'Right Hip',
  left_knee: 'Left Knee',
  right_knee: 'Right Knee',
  left_ankle: 'Left Ankle',
  right_ankle: 'Right Ankle',
  spine: 'Spine',
};

/**
 * Vector subtraction
 * @param {Object} a - First vector
 * @param {Object} b - Second vector
 * @returns {Object} - Result vector
 */
function sub(a, b) {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

/**
 * Dot product
 * @param {Object} a - First vector
 * @param {Object} b - Second vector
 * @returns {number} - Dot product
 */
function dot(a, b) {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/**
 * Vector magnitude
 * @param {Object} a - Vector
 * @returns {number} - Magnitude
 */
function norm(a) {
  return Math.sqrt(dot(a, a));
}

/**
 * Calculate angle at point B formed by vectors A-B and C-B
 * @param {Object} a - Point A
 * @param {Object} b - Point B (vertex)
 * @param {Object} c - Point C
 * @returns {number} - Angle in degrees
 */
function angleABC(a, b, c) {
  const v1 = sub(a, b);
  const v2 = sub(c, b);
  const n1 = norm(v1);
  const n2 = norm(v2);
  if (n1 === 0 || n2 === 0) return 0;
  const cosTheta = Math.max(-1, Math.min(1, dot(v1, v2) / (n1 * n2)));
  return (Math.acos(cosTheta) * 180) / Math.PI;
}

/**
 * Extract joint angles from landmarks
 * @param {Array} landmarks - Array of landmark objects
 * @returns {Object} - Object with joint names as keys and angles as values
 */
function extractAngles(landmarks) {
  const angles = {};

  // Elbow angles
  angles['left_elbow'] = angleABC(
    landmarks[LANDMARK_INDICES.LEFT_SHOULDER],
    landmarks[LANDMARK_INDICES.LEFT_ELBOW],
    landmarks[LANDMARK_INDICES.LEFT_WRIST]
  );
  angles['right_elbow'] = angleABC(
    landmarks[LANDMARK_INDICES.RIGHT_SHOULDER],
    landmarks[LANDMARK_INDICES.RIGHT_ELBOW],
    landmarks[LANDMARK_INDICES.RIGHT_WRIST]
  );

  // Shoulder angles (arm elevation)
  angles['left_shoulder'] = angleABC(
    landmarks[LANDMARK_INDICES.LEFT_HIP],
    landmarks[LANDMARK_INDICES.LEFT_SHOULDER],
    landmarks[LANDMARK_INDICES.LEFT_ELBOW]
  );
  angles['right_shoulder'] = angleABC(
    landmarks[LANDMARK_INDICES.RIGHT_HIP],
    landmarks[LANDMARK_INDICES.RIGHT_SHOULDER],
    landmarks[LANDMARK_INDICES.RIGHT_ELBOW]
  );

  // Hip angles
  angles['left_hip'] = angleABC(
    landmarks[LANDMARK_INDICES.LEFT_SHOULDER],
    landmarks[LANDMARK_INDICES.LEFT_HIP],
    landmarks[LANDMARK_INDICES.LEFT_KNEE]
  );
  angles['right_hip'] = angleABC(
    landmarks[LANDMARK_INDICES.RIGHT_SHOULDER],
    landmarks[LANDMARK_INDICES.RIGHT_HIP],
    landmarks[LANDMARK_INDICES.RIGHT_KNEE]
  );

  // Knee angles
  angles['left_knee'] = angleABC(
    landmarks[LANDMARK_INDICES.LEFT_HIP],
    landmarks[LANDMARK_INDICES.LEFT_KNEE],
    landmarks[LANDMARK_INDICES.LEFT_ANKLE]
  );
  angles['right_knee'] = angleABC(
    landmarks[LANDMARK_INDICES.RIGHT_HIP],
    landmarks[LANDMARK_INDICES.RIGHT_KNEE],
    landmarks[LANDMARK_INDICES.RIGHT_ANKLE]
  );

  // Ankle angles (knee-ankle-foot)
  angles['left_ankle'] = angleABC(
    landmarks[LANDMARK_INDICES.LEFT_KNEE],
    landmarks[LANDMARK_INDICES.LEFT_ANKLE],
    landmarks[LANDMARK_INDICES.LEFT_FOOT_INDEX]
  );
  angles['right_ankle'] = angleABC(
    landmarks[LANDMARK_INDICES.RIGHT_KNEE],
    landmarks[LANDMARK_INDICES.RIGHT_ANKLE],
    landmarks[LANDMARK_INDICES.RIGHT_FOOT_INDEX]
  );

  // Spine angle (midpoint shoulders to midpoint hips, relative to vertical)
  const midShoulder = {
    x: (landmarks[LANDMARK_INDICES.LEFT_SHOULDER].x + landmarks[LANDMARK_INDICES.RIGHT_SHOULDER].x) / 2,
    y: (landmarks[LANDMARK_INDICES.LEFT_SHOULDER].y + landmarks[LANDMARK_INDICES.RIGHT_SHOULDER].y) / 2,
    z: (landmarks[LANDMARK_INDICES.LEFT_SHOULDER].z + landmarks[LANDMARK_INDICES.RIGHT_SHOULDER].z) / 2,
  };
  const midHip = {
    x: (landmarks[LANDMARK_INDICES.LEFT_HIP].x + landmarks[LANDMARK_INDICES.RIGHT_HIP].x) / 2,
    y: (landmarks[LANDMARK_INDICES.LEFT_HIP].y + landmarks[LANDMARK_INDICES.RIGHT_HIP].y) / 2,
    z: (landmarks[LANDMARK_INDICES.LEFT_HIP].z + landmarks[LANDMARK_INDICES.RIGHT_HIP].z) / 2,
  };
  // Virtual point directly above midHip for vertical reference
  const aboveHip = { x: midHip.x, y: midHip.y - 1, z: midHip.z };
  angles['spine'] = angleABC(midShoulder, midHip, aboveHip);

  return angles;
}

/**
 * Calculate wrist velocity for movement tracking
 * @param {Array} current - Current frame landmarks
 * @param {Array} previous - Previous frame landmarks
 * @param {number} timeDeltaMs - Time difference in milliseconds
 * @returns {Object} - Speed and direction
 */
function calculateWristVelocity(current, previous, timeDeltaMs) {
  if (timeDeltaMs === 0) return { speed: 0, direction: 'stationary' };

  const currWrist = current[LANDMARK_INDICES.RIGHT_WRIST];
  const prevWrist = previous[LANDMARK_INDICES.RIGHT_WRIST];

  const dx = currWrist.x - prevWrist.x;
  const dy = currWrist.y - prevWrist.y;
  const dz = currWrist.z - prevWrist.z;

  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const speed = distance / (timeDeltaMs / 1000);

  let direction = 'stationary';
  const maxDelta = Math.max(Math.abs(dx), Math.abs(dy), Math.abs(dz));
  if (maxDelta > 0.01) {
    if (Math.abs(dy) === maxDelta) {
      direction = dy < 0 ? 'upward' : 'downward';
    } else if (Math.abs(dx) === maxDelta) {
      direction = dx > 0 ? 'rightward' : 'leftward';
    } else {
      direction = dz > 0 ? 'forward' : 'backward';
    }
  }

  return { speed, direction };
}

/**
 * Extract key body positions (normalized 0-1, where 0=top, 1=bottom for Y)
 * @param {Array} landmarks - Array of landmark objects
 * @returns {Object} - Position metrics
 */
function extractPositions(landmarks) {
  const leftWrist = landmarks[LANDMARK_INDICES.LEFT_WRIST];
  const rightWrist = landmarks[LANDMARK_INDICES.RIGHT_WRIST];
  const leftHip = landmarks[LANDMARK_INDICES.LEFT_HIP];
  const rightHip = landmarks[LANDMARK_INDICES.RIGHT_HIP];
  const leftShoulder = landmarks[LANDMARK_INDICES.LEFT_SHOULDER];
  const rightShoulder = landmarks[LANDMARK_INDICES.RIGHT_SHOULDER];
  const leftAnkle = landmarks[LANDMARK_INDICES.LEFT_ANKLE];
  const rightAnkle = landmarks[LANDMARK_INDICES.RIGHT_ANKLE];

  // Wrist heights (inverted: 1 = high, 0 = low)
  const leftWristHeight = 1 - leftWrist.y;
  const rightWristHeight = 1 - rightWrist.y;
  
  // Use dominant (higher) wrist for shooting analysis
  const dominantWristHeight = Math.max(leftWristHeight, rightWristHeight);

  // Hip height (center of hips, inverted)
  const hipHeight = 1 - (leftHip.y + rightHip.y) / 2;

  // Shoulder width (horizontal distance between shoulders)
  const shoulderWidth = Math.abs(rightShoulder.x - leftShoulder.x);

  // Stance width (horizontal distance between ankles)
  const stanceWidth = Math.abs(rightAnkle.x - leftAnkle.x);

  // Center of mass approximation (weighted average of key points)
  const comX = (leftHip.x + rightHip.x + leftShoulder.x + rightShoulder.x) / 4;
  const comY = 1 - (leftHip.y + rightHip.y + leftShoulder.y + rightShoulder.y) / 4;

  return {
    wristHeight: parseFloat(dominantWristHeight.toFixed(2)),
    leftWristHeight: parseFloat(leftWristHeight.toFixed(2)),
    rightWristHeight: parseFloat(rightWristHeight.toFixed(2)),
    hipHeight: parseFloat(hipHeight.toFixed(2)),
    shoulderWidth: parseFloat(shoulderWidth.toFixed(2)),
    stanceWidth: parseFloat(stanceWidth.toFixed(2)),
    centerOfMass: {
      x: parseFloat(comX.toFixed(2)),
      y: parseFloat(comY.toFixed(2)),
    },
  };
}

/**
 * Calculate body orientation and balance metrics
 * @param {Array} landmarks - Array of landmark objects
 * @returns {Object} - Orientation metrics
 */
function extractOrientation(landmarks) {
  const leftShoulder = landmarks[LANDMARK_INDICES.LEFT_SHOULDER];
  const rightShoulder = landmarks[LANDMARK_INDICES.RIGHT_SHOULDER];
  const leftHip = landmarks[LANDMARK_INDICES.LEFT_HIP];
  const rightHip = landmarks[LANDMARK_INDICES.RIGHT_HIP];

  // Shoulder tilt (positive = right shoulder higher)
  const shoulderDeltaY = leftShoulder.y - rightShoulder.y;
  const shoulderDeltaX = rightShoulder.x - leftShoulder.x;
  const shoulderTiltRad = Math.atan2(shoulderDeltaY, shoulderDeltaX);
  const shoulderTilt = (shoulderTiltRad * 180) / Math.PI;

  // Hip tilt (positive = right hip higher)
  const hipDeltaY = leftHip.y - rightHip.y;
  const hipDeltaX = rightHip.x - leftHip.x;
  const hipTiltRad = Math.atan2(hipDeltaY, hipDeltaX);
  const hipTilt = (hipTiltRad * 180) / Math.PI;

  // Torso lean (forward/backward from vertical)
  // Calculate midpoints
  const midShoulder = {
    x: (leftShoulder.x + rightShoulder.x) / 2,
    y: (leftShoulder.y + rightShoulder.y) / 2,
    z: (leftShoulder.z + rightShoulder.z) / 2,
  };
  const midHip = {
    x: (leftHip.x + rightHip.x) / 2,
    y: (leftHip.y + rightHip.y) / 2,
    z: (leftHip.z + rightHip.z) / 2,
  };

  // Forward lean (using z-axis, positive = leaning forward)
  const forwardLean = (midHip.z - midShoulder.z) * 100; // Scale for readability

  // Lateral lean (using x-axis deviation from vertical)
  const lateralLeanRad = Math.atan2(midShoulder.x - midHip.x, midHip.y - midShoulder.y);
  const lateralLean = (lateralLeanRad * 180) / Math.PI;

  // Hip rotation (comparing hip line to shoulder line in x-z plane)
  const shoulderAngle = Math.atan2(rightShoulder.z - leftShoulder.z, rightShoulder.x - leftShoulder.x);
  const hipAngle = Math.atan2(rightHip.z - leftHip.z, rightHip.x - leftHip.x);
  const hipRotation = ((hipAngle - shoulderAngle) * 180) / Math.PI;

  return {
    shoulderTilt: parseFloat(shoulderTilt.toFixed(1)),
    hipTilt: parseFloat(hipTilt.toFixed(1)),
    forwardLean: parseFloat(forwardLean.toFixed(1)),
    lateralLean: parseFloat(lateralLean.toFixed(1)),
    hipRotation: parseFloat(hipRotation.toFixed(1)),
  };
}

/**
 * Calculate detailed velocity data between frames
 * @param {Array} current - Current frame landmarks
 * @param {Array} previous - Previous frame landmarks
 * @param {number} timeDeltaMs - Time difference in milliseconds
 * @returns {Object} - Detailed velocity metrics
 */
function calculateDetailedVelocity(current, previous, timeDeltaMs) {
  if (timeDeltaMs === 0) {
    return {
      wrist: { speed: 0, direction: 'stationary', vx: 0, vy: 0, vz: 0 },
      hip: { speed: 0, vy: 0 },
      shoulder: { speed: 0 },
    };
  }

  const timeSeconds = timeDeltaMs / 1000;

  // Wrist velocity (dominant hand - right)
  const currWrist = current[LANDMARK_INDICES.RIGHT_WRIST];
  const prevWrist = previous[LANDMARK_INDICES.RIGHT_WRIST];
  const wristDx = currWrist.x - prevWrist.x;
  const wristDy = currWrist.y - prevWrist.y;
  const wristDz = currWrist.z - prevWrist.z;
  const wristDistance = Math.sqrt(wristDx * wristDx + wristDy * wristDy + wristDz * wristDz);
  const wristSpeed = wristDistance / timeSeconds;

  let wristDirection = 'stationary';
  const maxWristDelta = Math.max(Math.abs(wristDx), Math.abs(wristDy), Math.abs(wristDz));
  if (maxWristDelta > 0.01) {
    if (Math.abs(wristDy) === maxWristDelta) {
      wristDirection = wristDy < 0 ? 'upward' : 'downward';
    } else if (Math.abs(wristDx) === maxWristDelta) {
      wristDirection = wristDx > 0 ? 'rightward' : 'leftward';
    } else {
      wristDirection = wristDz > 0 ? 'forward' : 'backward';
    }
  }

  // Hip velocity (center of hips - for jump detection)
  const currHipY = (current[LANDMARK_INDICES.LEFT_HIP].y + current[LANDMARK_INDICES.RIGHT_HIP].y) / 2;
  const prevHipY = (previous[LANDMARK_INDICES.LEFT_HIP].y + previous[LANDMARK_INDICES.RIGHT_HIP].y) / 2;
  const hipDy = currHipY - prevHipY;
  const hipSpeed = Math.abs(hipDy) / timeSeconds;

  // Shoulder velocity (for upper body movement)
  const currShoulderY = (current[LANDMARK_INDICES.LEFT_SHOULDER].y + current[LANDMARK_INDICES.RIGHT_SHOULDER].y) / 2;
  const prevShoulderY = (previous[LANDMARK_INDICES.LEFT_SHOULDER].y + previous[LANDMARK_INDICES.RIGHT_SHOULDER].y) / 2;
  const shoulderDy = currShoulderY - prevShoulderY;
  const shoulderSpeed = Math.abs(shoulderDy) / timeSeconds;

  return {
    wrist: {
      speed: parseFloat(wristSpeed.toFixed(2)),
      direction: wristDirection,
      vx: parseFloat((wristDx / timeSeconds).toFixed(2)),
      vy: parseFloat((-wristDy / timeSeconds).toFixed(2)), // Invert so positive = up
      vz: parseFloat((wristDz / timeSeconds).toFixed(2)),
    },
    hip: {
      speed: parseFloat(hipSpeed.toFixed(2)),
      vy: parseFloat((-hipDy / timeSeconds).toFixed(2)), // Invert so positive = up (jumping)
    },
    shoulder: {
      speed: parseFloat(shoulderSpeed.toFixed(2)),
      vy: parseFloat((-shoulderDy / timeSeconds).toFixed(2)),
    },
  };
}

/**
 * Calculate average confidence/visibility score
 * @param {Array} landmarks - Array of landmark objects
 * @returns {Object} - Confidence metrics
 */
function extractConfidence(landmarks) {
  const visibilities = landmarks
    .filter((lm) => lm.visibility !== undefined)
    .map((lm) => lm.visibility);

  if (visibilities.length === 0) {
    return { average: 1.0, lowConfidenceJoints: [] };
  }

  const average = visibilities.reduce((a, b) => a + b, 0) / visibilities.length;

  // Find joints with low confidence
  const lowConfidenceThreshold = 0.5;
  const lowConfidenceJoints = [];
  
  const keyJointsToCheck = [
    { idx: LANDMARK_INDICES.LEFT_WRIST, name: 'left_wrist' },
    { idx: LANDMARK_INDICES.RIGHT_WRIST, name: 'right_wrist' },
    { idx: LANDMARK_INDICES.LEFT_ELBOW, name: 'left_elbow' },
    { idx: LANDMARK_INDICES.RIGHT_ELBOW, name: 'right_elbow' },
    { idx: LANDMARK_INDICES.LEFT_SHOULDER, name: 'left_shoulder' },
    { idx: LANDMARK_INDICES.RIGHT_SHOULDER, name: 'right_shoulder' },
    { idx: LANDMARK_INDICES.LEFT_HIP, name: 'left_hip' },
    { idx: LANDMARK_INDICES.RIGHT_HIP, name: 'right_hip' },
    { idx: LANDMARK_INDICES.LEFT_KNEE, name: 'left_knee' },
    { idx: LANDMARK_INDICES.RIGHT_KNEE, name: 'right_knee' },
    { idx: LANDMARK_INDICES.LEFT_ANKLE, name: 'left_ankle' },
    { idx: LANDMARK_INDICES.RIGHT_ANKLE, name: 'right_ankle' },
  ];

  keyJointsToCheck.forEach(({ idx, name }) => {
    const visibility = landmarks[idx]?.visibility;
    if (visibility !== undefined && visibility < lowConfidenceThreshold) {
      lowConfidenceJoints.push({ joint: name, confidence: parseFloat(visibility.toFixed(2)) });
    }
  });

  return {
    average: parseFloat(average.toFixed(2)),
    lowConfidenceJoints,
  };
}

/**
 * Convert pose frames to LLM-friendly format
 * @param {Array} poses - Array of pose frames
 * @param {string} recordingName - Name of the recording
 * @returns {Object} - LLM-friendly pose analysis
 */
export function convertToLLMFormat(poses, recordingName) {
  if (!poses || poses.length === 0) {
    throw new Error('No pose frames to convert');
  }

  const startTime = poses[0].timestampMs;
  const endTime = poses[poses.length - 1].timestampMs;
  const durationMs = endTime - startTime;
  const durationSeconds = durationMs / 1000;
  const fps = poses.length / Math.max(durationSeconds, 0.001);

  // Convert frames
  const llmFrames = poses.map((frame, idx) => {
    const prevFrame = idx > 0 ? poses[idx - 1] : null;
    const timeSeconds = (frame.timestampMs - startTime) / 1000;

    const angles = extractAngles(frame.landmarks);
    const positions = extractPositions(frame.landmarks);
    const orientation = extractOrientation(frame.landmarks);
    const confidence = extractConfidence(frame.landmarks);

    // Build joints array with readable names
    const joints = Object.entries(angles).map(([key, value]) => {
      const name = JOINT_NAMES[key] || key;
      return {
        name,
        angle: `${value.toFixed(1)}°`,
      };
    });

    // Calculate movement/velocity
    let velocity = undefined;
    if (prevFrame) {
      const timeDelta = frame.timestampMs - prevFrame.timestampMs;
      velocity = calculateDetailedVelocity(
        frame.landmarks,
        prevFrame.landmarks,
        timeDelta
      );
    }

    return {
      frameNumber: idx + 1,
      timeSeconds: parseFloat(timeSeconds.toFixed(3)),
      joints,
      positions,
      orientation,
      velocity,
      confidence: confidence.average,
      lowConfidenceJoints: confidence.lowConfidenceJoints.length > 0 ? confidence.lowConfidenceJoints : undefined,
    };
  });

  return {
    metadata: {
      recordingName,
      totalFrames: poses.length,
      durationSeconds: parseFloat(durationSeconds.toFixed(2)),
      framesPerSecond: parseFloat(fps.toFixed(1)),
      exportedAt: new Date().toISOString(),
      format: 'LLM-friendly pose analysis v1.0',
    },
    context: {
      sport: 'Basketball',
      activity: 'Movement Analysis',
      description:
        'This document contains frame-by-frame biomechanical data extracted from a recorded movement. Each frame includes joint angles, body positions, orientation/balance metrics, and velocity data. Use this data to analyze form, body mechanics, and movement patterns.',
      metrics: {
        angles: ['Elbow', 'Shoulder', 'Hip', 'Knee', 'Ankle', 'Spine'],
        positions: ['Wrist Height (0-1, higher=up)', 'Hip Height', 'Shoulder Width', 'Stance Width', 'Center of Mass'],
        orientation: ['Shoulder Tilt (°)', 'Hip Tilt (°)', 'Forward Lean', 'Lateral Lean (°)', 'Hip Rotation (°)'],
        velocity: ['Wrist Speed (units/s)', 'Hip Velocity (jumping)', 'Shoulder Velocity'],
      },
      keyJoints: [
        'Right Elbow',
        'Right Shoulder',
        'Right Knee',
        'Left Elbow',
        'Left Shoulder',
        'Left Knee',
        'Right Hip',
        'Left Hip',
        'Spine',
      ],
    },
    frames: llmFrames,
  };
}

/**
 * Download the LLM-friendly analysis as a JSON file
 * @param {Array} poses - Array of pose frames
 * @param {string} recordingName - Name of the recording
 */
export function downloadLLMAnalysis(poses, recordingName) {
  const analysis = convertToLLMFormat(poses, recordingName);
  const json = JSON.stringify(analysis, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${recordingName.replace(/\s+/g, '_')}_pose_analysis.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Create a condensed summary of pose data for LLM prompt (to save tokens)
 * @param {Array} poses - Array of pose frames
 * @param {string} recordingName - Name of the recording
 * @returns {string} - Condensed text summary
 */
export function createPoseSummaryForPrompt(poses, recordingName) {
  if (!poses || poses.length === 0) {
    return '';
  }

  const analysis = convertToLLMFormat(poses, recordingName);
  
  // Create a condensed summary
  const lines = [
    `POSE ANALYSIS DATA (${analysis.metadata.totalFrames} frames, ${analysis.metadata.durationSeconds}s):`,
    ''
  ];

  // Sample key frames (first, middle, last and some in between)
  const keyFrameIndices = [0];
  const step = Math.max(1, Math.floor(analysis.frames.length / 5));
  for (let i = step; i < analysis.frames.length - 1; i += step) {
    keyFrameIndices.push(i);
  }
  if (analysis.frames.length > 1) {
    keyFrameIndices.push(analysis.frames.length - 1);
  }

  keyFrameIndices.forEach(idx => {
    const frame = analysis.frames[idx];
    
    // Key angles (condensed - only main joints for token efficiency)
    const keyAngles = frame.joints
      .filter(j => ['Left Elbow', 'Right Elbow', 'Left Shoulder', 'Right Shoulder', 'Spine'].includes(j.name))
      .map(j => `${j.name}: ${j.angle}`)
      .join(', ');
    
    // Positions
    const pos = frame.positions;
    const positionSummary = `WristH: ${pos.wristHeight}, HipH: ${pos.hipHeight}, Stance: ${pos.stanceWidth}`;
    
    // Orientation
    const orient = frame.orientation;
    const orientSummary = `Lean: ${orient.forwardLean > 0 ? '+' : ''}${orient.forwardLean}° fwd, ShoulderTilt: ${orient.shoulderTilt}°`;
    
    lines.push(`Frame ${frame.frameNumber} (${frame.timeSeconds}s):`);
    lines.push(`  Angles: ${keyAngles}`);
    lines.push(`  Position: ${positionSummary}`);
    lines.push(`  Balance: ${orientSummary}`);
    
    // Velocity (if available)
    if (frame.velocity) {
      const vel = frame.velocity;
      const speedCategory = vel.wrist.speed > 0.5 ? 'fast' : vel.wrist.speed > 0.2 ? 'moderate' : 'slow';
      const jumpIndicator = vel.hip.vy > 0.3 ? ' [JUMPING]' : vel.hip.vy < -0.3 ? ' [LANDING]' : '';
      lines.push(`  Movement: ${vel.wrist.direction} (${speedCategory}, ${vel.wrist.speed} u/s)${jumpIndicator}`);
    }
    
    // Low confidence warning
    if (frame.lowConfidenceJoints && frame.lowConfidenceJoints.length > 0) {
      const lowConf = frame.lowConfidenceJoints.map(j => j.joint).join(', ');
      lines.push(`  ⚠️ Low confidence: ${lowConf}`);
    }
  });

  return lines.join('\n');
}
